import type { CmsSelection } from "../types";

export class CmsSelectionOverlay {
    readonly #root: HTMLElement;
    readonly #host: HTMLElement;
    readonly #hoverBox: HTMLElement;
    readonly #selectedBox: HTMLElement;
    readonly #resizeObserver: ResizeObserver;
    readonly #mutationObserver: MutationObserver;
    #hovered: CmsSelection | null = null;
    #selected: CmsSelection | null = null;
    #frame = 0;

    constructor(root: HTMLElement, host: HTMLElement) {
        this.#host = host;
        this.#root = document.createElement("div");
        this.#root.className = "admin-cms-selection-overlay";
        this.#root.dataset.cmsSelectionOverlay = "1";
        this.#root.setAttribute("aria-hidden", "true");

        this.#hoverBox = this.createBox("hover");
        this.#selectedBox = this.createBox("selected");
        this.#root.append(this.#hoverBox, this.#selectedBox);
        root.appendChild(this.#root);

        this.#resizeObserver = new ResizeObserver(() => this.schedule());
        this.#mutationObserver = new MutationObserver(() => this.schedule());
    }

    static selectionFromElement(element: HTMLElement): CmsSelection {
        const type = (element.dataset.cmsEditableType ?? "section") as CmsSelection["type"];
        const id = element.dataset.cmsEditableId ?? "";
        const label = element.dataset.title || element.dataset.typeLabel || (type === "section" ? "섹션" : "블록");
        return {
            type,
            id,
            label,
            sublabel: element.dataset.typeLabel,
            element,
        };
    }

    mount(): void {
        window.addEventListener("resize", this.scheduleBound, { passive: true });
        window.addEventListener("scroll", this.scheduleBound, { passive: true, capture: true });
        this.#host.addEventListener("load", this.scheduleBound, true);
        this.#resizeObserver.observe(this.#host);
        this.#mutationObserver.observe(this.#host, {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: true,
        });
        if ("fonts" in document) {
            void document.fonts.ready.then(() => this.schedule());
        }
        this.schedule();
    }

    destroy(): void {
        window.removeEventListener("resize", this.scheduleBound);
        window.removeEventListener("scroll", this.scheduleBound, true);
        this.#host.removeEventListener("load", this.scheduleBound, true);
        this.#resizeObserver.disconnect();
        this.#mutationObserver.disconnect();
        if (this.#frame !== 0) {
            window.cancelAnimationFrame(this.#frame);
            this.#frame = 0;
        }
        this.#root.remove();
    }

    setHovered(selection: CmsSelection | null): void {
        this.#hovered = selection;
        this.schedule();
    }

    setSelected(selection: CmsSelection | null): void {
        this.#selected = selection;
        this.schedule();
    }

    schedule(): void {
        if (this.#frame !== 0) return;
        this.#frame = window.requestAnimationFrame(() => {
            this.#frame = 0;
            this.update();
        });
    }

    private readonly scheduleBound = (): void => {
        this.schedule();
    };

    private createBox(kind: "hover" | "selected"): HTMLElement {
        const box = document.createElement("div");
        box.className = `admin-cms-selection-overlay__box admin-cms-selection-overlay__box--${kind}`;
        box.hidden = true;
        const label = document.createElement("span");
        label.className = "admin-cms-selection-overlay__label";
        box.appendChild(label);
        return box;
    }

    private update(): void {
        this.place(this.#hoverBox, this.#hovered, this.#selected?.element ?? null);
        this.place(this.#selectedBox, this.#selected, null);
    }

    private place(box: HTMLElement, selection: CmsSelection | null, skipElement: HTMLElement | null): void {
        const element = selection?.element ?? null;
        if (!element || element === skipElement || !document.documentElement.contains(element)) {
            box.hidden = true;
            return;
        }

        const rect = element.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            box.hidden = true;
            return;
        }

        const rootRect = this.#host.getBoundingClientRect();
        const left = Math.max(rootRect.left, rect.left);
        const top = Math.max(rootRect.top, rect.top);
        const right = Math.min(rootRect.right, rect.right);
        const bottom = Math.min(rootRect.bottom, rect.bottom);
        const width = Math.max(0, right - left);
        const height = Math.max(0, bottom - top);
        if (width <= 0 || height <= 0) {
            box.hidden = true;
            return;
        }

        box.hidden = false;
        box.style.left = `${left}px`;
        box.style.top = `${top}px`;
        box.style.width = `${width}px`;
        box.style.height = `${height}px`;
        const label = box.querySelector<HTMLElement>(".admin-cms-selection-overlay__label");
        if (label) {
            label.textContent = this.label(selection);
            label.classList.toggle("admin-cms-selection-overlay__label--below", top < 28);
        }
    }

    private label(selection: CmsSelection): string {
        const element = selection.element;
        const kind = this.kindLabel(selection);
        const name = selection.label || element?.dataset.typeLabel || element?.dataset.title || selection.id;
        return name !== "" ? `${kind} · ${name}` : kind;
    }

    private kindLabel(selection: CmsSelection): string {
        if (selection.element?.dataset.cmsNodeType === "collection" || selection.element?.dataset.cmsCollection === "1") return "Collection";
        if (selection.type === "section") return "Section";
        if (selection.type === "template-field") {
            const type = selection.fieldType ?? selection.element?.dataset.cmsTemplateFieldType ?? "text";
            if (type === "media" || type === "image") return "Image";
            if (selection.element?.closest("[data-cms-collection-item]")) return "Template Item";
            return "Text";
        }
        const elementType = selection.element?.dataset.elementType ?? selection.sublabel ?? "";
        if (elementType === "image" || elementType === "gallery" || elementType === "media") return "Image";
        if (elementType === "button") return "Button";
        if (elementType === "template_component") return "Container";
        if (elementType === "html") return "Embed";
        return "Element";
    }
}
